import { FC } from 'react';
import {
  Box,
  FormHelperText,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Tooltip,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { PermissionScope } from 'types/apps';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { getDisabledPermissionCopy } from 'utils/permissions';
import { HookFormComponentProps } from 'ui-component/HookFormComponents/types';
import _ from 'lodash';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((__) => ({
  textarea: {
    resize: 'both',
  },
}));

export type HookFormInputProps = {
  multiline?: boolean;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  permissionScope?: PermissionScope;
  boxSx?: SxProps<Theme>;
  additionalOnChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  resize?: boolean;
  helperText?: string;
} & TextFieldProps &
  HookFormComponentProps;

export const HookFormInput: FC<HookFormInputProps> = ({
  errors,
  name = '',
  label,
  control,
  fullWidth = true,
  multiline = false,
  rows = 1,
  select = false,
  type = 'text',
  inputProps = {},
  InputProps = {},
  InputLabelProps = {},
  disabled = false,
  children,
  sx,
  boxSx,
  size = 'medium',
  defaultValue,
  onKeyDown,
  permissionScope,
  additionalOnChange,
  variant = 'outlined',
  resize = false,
  helperText,
}) => {
  const { isPermissionDisabled, getDisabledReason } = useAppAccessContext();

  const permissionDisabled = isPermissionDisabled(permissionScope);
  const disabledCopy =
    permissionScope &&
    permissionDisabled &&
    getDisabledPermissionCopy(getDisabledReason(permissionScope));

  const classes = useStyles();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, onBlur } }) => (
        <Tooltip
          title={
            permissionScope &&
            permissionDisabled &&
            disabledCopy &&
            `${disabledCopy?.message} ${disabledCopy?.action}`
          }
        >
          <Box sx={{ mb: 2, ...boxSx }}>
            <TextField
              variant={variant}
              fullWidth={fullWidth}
              sx={sx}
              size={size}
              name={name}
              value={permissionDisabled ? '' : value ?? ''}
              onBlur={onBlur}
              onChange={(e) => {
                onChange(e);
                additionalOnChange && additionalOnChange(e);
              }}
              error={!!_.get(errors, name)}
              aria-describedby={`${name}Error`}
              label={label}
              multiline={multiline}
              rows={rows}
              select={select}
              type={type}
              inputProps={{
                ...inputProps,
                ...(resize ? { className: classes.textarea } : {}),
              }}
              /* eslint-disable-next-line react/jsx-no-duplicate-props */
              InputProps={InputProps}
              InputLabelProps={InputLabelProps}
              disabled={permissionDisabled || disabled}
              defaultValue={defaultValue}
              onKeyDown={onKeyDown}
            >
              {children}
            </TextField>
            {helperText && (
              <FormHelperText id={`${name}HelperText`}>
                {helperText}
              </FormHelperText>
            )}
            {_.get(errors, name)?.message && (
              <FormHelperText error id={`${name}Error`}>
                {_.get(errors, name)?.message}
              </FormHelperText>
            )}
          </Box>
        </Tooltip>
      )}
    />
  );
};
